import React, { useEffect, useState } from 'react';
import { formatDateTime, getStatusColor, validateOrderCalculations } from '../utils/helpers';
import { getClients, getWholesalerById } from '../services/api';
import { useAuth } from '../context/AuthContext';

const formatNumber = (num) => Number(num).toLocaleString('en-US');
const formatNumberNoRound = (value) => {
    if (value === null || value === undefined) return '';
    const str = typeof value === 'string' ? value : String(value);
    const clean = str.replace(/,/g, '');
    if (!clean) return '';
    const parts = clean.split('.');
    const intPart = parts[0] || '';
    const fracPart = parts[1] !== undefined ? parts[1] : undefined;
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fracPart !== undefined && fracPart !== '' ? `${formattedInt}.${fracPart}` : formattedInt;
};

const warehouseMap = {
    EBL_WH: 'Erbil Warehouse',
    DUH_WH: 'Dohuk Warehouse',
    SUL_WH: 'Suly Warehouse',
    KRK_WH: 'Kirkuk Warehouse',
    MSL_WH: 'Mosul Warehouse',
    BGD_WH: 'Baghdad Warehouse',
};

const OrderDetailsView = ({ order, onStatusUpdate, children }) => {
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(true);
    const { accessToken } = useAuth();
    const [merchantName, setMerchantName] = useState(order.merchantName || '');
    const [merchantLoading, setMerchantLoading] = useState(false);
    const [merchantError, setMerchantError] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [attachmentError, setAttachmentError] = useState('');

    // Validate backend totals against frontend calculations
    useEffect(() => {
        if (order) {
            const validation = validateOrderCalculations(order);

            if (!validation.isValid) {
                console.warn('Total calculation discrepancy detected:', {
                    orderId: order.id,
                    validation,
                    order
                });
            } else {
                console.log('Order calculations validated successfully:', {
                    orderId: order.id,
                    total: validation.total
                });
            }
        }
    }, [order]);

    useEffect(() => {
        if (!order.merchantName && order.wholesalerId) {
            setMerchantLoading(true);
            setMerchantError('');
            getWholesalerById(order.wholesalerId)
                .then(data => setMerchantName(data.merchantName || ''))
                .catch(() => setMerchantError('Could not fetch wholesaler name'))
                .finally(() => setMerchantLoading(false));
        } else if (order.merchantName) {
            setMerchantName(order.merchantName);
        }
    }, [order.merchantName, order.wholesalerId]);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                setClientsLoading(true);
                const data = await getClients();
                setClients(data);
            } catch {
                setClients([]);
            } finally {
                setClientsLoading(false);
            }
        };
        fetchClients();
        // Removed window focus event listener
    }, [accessToken]);

    if (!order) return null;

    // Group E-PIN orders by clientId
    let epinByClient = {};
    if (order.epinOrders && order.epinOrders.length > 0) {
        order.epinOrders.forEach((epin) => {
            if (!epinByClient[epin.clientId]) epinByClient[epin.clientId] = [];
            epinByClient[epin.clientId].push(epin);
        });
    }

    return (
        <div className="bg-white p-4 sm:p-8 rounded-lg shadow-sm border border-gray-200 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Order Details</h2>
            <div className="mb-4 flex flex-col gap-2">
                <div><span className="font-semibold">Order ID:</span> {order.id}</div>
                <div>
                    <span className="font-semibold">Status:</span> <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
                </div>
                {(order.assignedDealer || order.dealerName) && (
                    <div><span className="font-semibold">Dealer Name:</span> {order.assignedDealer || order.dealerName}</div>
                )}
                <div><span className="font-semibold">Created:</span> {formatDateTime(order.createdAt)}</div>
                {order.wholesalerId && (
                    <>
                        <div><span className="font-semibold">Wholesaler ID:</span> {order.wholesalerId}</div>
                        {(merchantLoading ? (
                            <div className="text-sm text-gray-500">Loading wholesaler name...</div>
                        ) : merchantName ? (
                            <div><span className="font-semibold">Wholesaler Name:</span> {merchantName}</div>
                        ) : merchantError ? (
                            <div className="text-sm text-red-500">{merchantError}</div>
                        ) : null)}
                    </>
                )}
                {!order.wholesalerId && merchantName && (
                    <div><span className="font-semibold">Wholesaler Name:</span> {merchantName}</div>
                )}
            </div>

            {/* Scratch Card Orders */}
            {order.scratchCardOrders && order.scratchCardOrders.length > 0 && (
                <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-2">Scratch Card Orders</h3>
                    <div className="mb-2 text-gray-700 text-sm">Total: <span className="font-bold">IQD {formatNumber(order.scratchAmountToBePaid)}</span></div>
                    <div className="overflow-x-auto w-full border border-gray-200 rounded-lg mb-2 shadow-sm">
                        <table className="min-w-full text-sm divide-y divide-gray-200">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Warehouse</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Denomination</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {order.scratchCardOrders
                                    .sort((a, b) => a.denomination - b.denomination)
                                    .map((item, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap text-gray-700">{warehouseMap[item.warehouse] || item.warehouse}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 font-medium tabular-nums">IQD {formatNumber(item.denomination)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">IQD {formatNumber(item.denominationPrice)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">{item.quantity}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 font-semibold tabular-nums">IQD {formatNumber(item.paidAmount)}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* E-PIN Orders by Client */}
            {order.epinOrders && order.epinOrders.length > 0 && (
                <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-2">E-PIN Orders</h3>
                    <div className="mb-2 text-gray-700 text-sm">Total: <span className="font-bold">IQD {formatNumber(order.epinAmountToBePaid)}</span></div>
                    {clientsLoading ? (
                        <div className="text-gray-500 text-sm mb-2">Loading clients...</div>
                    ) : (
                        Object.entries(epinByClient).map(([clientId, epins]) => {
                            const client = clients.find(c => c.id === clientId);
                            // Sort epins by denomination value (smallest to largest)
                            const sortedEpins = [...epins].sort((a, b) => a.denomination - b.denomination);
                            return (
                                <div key={clientId} className="mb-4">
                                    <div className="font-bold text-blue-900 text-sm mb-2">{client ? client.name : 'Unknown Client'}</div>
                                    <div className="overflow-x-auto w-full border border-gray-200 rounded-lg mb-2 shadow-sm">
                                        <table className="min-w-full text-sm divide-y divide-gray-200">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Denomination</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {sortedEpins.map((item, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 font-medium tabular-nums">IQD {formatNumber(item.denomination)}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">IQD {formatNumber(item.denominationPrice)}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">{item.quantity}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 font-semibold tabular-nums">IQD {formatNumber(item.paidAmount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* E-TopUp Order */}
            {(() => {
                // Handle both new eTopUp structure and old etopUpOrder structure
                const eTopUpData = order.eTopUp || order.etopUpOrder;
                const hasETopUp = (eTopUpData && eTopUpData.amount) || order.etopUpAmount;

                if (!hasETopUp) return null;

                return (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold mb-2">E-TopUp Order</h3>
                        <div className="mb-2 text-gray-700 text-sm">Total: <span className="font-bold">IQD {formatNumberNoRound(eTopUpData?.paidAmount ?? order.etopUpAmountToBePaid ?? ((eTopUpData?.amount || order.etopUpAmount) * 1.025))}</span></div>
                        <div className="overflow-x-auto w-full border border-gray-200 rounded-lg mb-2 shadow-sm">
                            <table className="w-full text-sm divide-y divide-gray-200" style={{ tableLayout: 'fixed', minWidth: '600px' }}>
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>Amount</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ width: '50%' }}>Note</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>Amount To Be Paid</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 text-xs text-gray-900 font-medium tabular-nums">IQD {formatNumber(eTopUpData?.amount || order.etopUpAmount)}</td>
                                        <td className="px-4 py-3 text-gray-700" style={{ wordBreak: 'break-word', whiteSpace: 'normal', overflowWrap: 'break-word' }}>{(eTopUpData?.note ?? order.note) || '—'}</td>
                                        <td className="px-4 py-3 text-xs text-gray-900 font-semibold tabular-nums">IQD {formatNumberNoRound(eTopUpData?.paidAmount ?? order.etopUpAmountToBePaid ?? ((eTopUpData?.amount || order.etopUpAmount) * 1.025))}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })()}

            {/* Grand Total */}
            <div className="mt-8 flex justify-end">
                {order.discountDetails && order.discountDetails.paidAmountAfterDiscount != null ? (
                    <div className="flex flex-col items-end">
                        <div className="text-xl font-bold text-gray-900 mb-1">
                            Grand Total
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 line-through text-lg">
                                IQD {formatNumber(order.discountDetails.paidPrice)}
                            </span>
                            {(Number(order.discountDetails.discountByPercentage) > 0 || Number(order.discountDetails.discountByAmount) > 0) && (
                                <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-100">
                                    -{Number(order.discountDetails.discountByPercentage) > 0
                                        ? `${Number(order.discountDetails.discountByPercentage)}%`
                                        : `IQD ${formatNumber(order.discountDetails.discountByAmount)}`}
                                </span>
                            )}
                        </div>
                        <div className="text-2xl font-bold text-gray-900">
                            IQD {formatNumber(order.discountDetails.paidAmountAfterDiscount)}
                        </div>
                    </div>
                ) : (
                    <div className="text-xl font-bold text-right text-gray-900">
                        {(() => {
                            if (order.totalAmountToBePaid != null) {
                                return <>Grand Total: IQD {formatNumber(order.totalAmountToBePaid)}</>;
                            }
                            if (order.totalAmount != null) {
                                return <>Grand Total: IQD {formatNumber(order.totalAmount)}</>;
                            }
                            return <>Grand Total: N/A</>;
                        })()}
                    </div>
                )}
            </div>

            {children}
        </div>
    );
};

export default OrderDetailsView;
